/**
 * Country names for the phone field's picker.
 *
 * `Intl.DisplayNames` is the right source and is present in every browser. It is
 * **not** present in Hermes, so on the phone every country rendered as its raw
 * ISO code — a list of 250 two-letter abbreviations, in which searching for
 * "israel" matched nothing and the only way to find IL was to type 972. A
 * picker nobody can search is not a picker.
 *
 * So the polyfill is loaded when the runtime lacks the API. It costs about
 * 145 KB per locale and only the active locale's data is registered.
 *
 * Search matches the localised name, the English name, the ISO code *and* the
 * calling code. English is always included on purpose: people reach for the
 * English name of their own country far more often than a monolingual design
 * would predict — which is exactly what happened here, in a Hebrew UI.
 */
import { countryDialInfo, dialInfoFor } from '@vav/protocol';

export interface CountryOption {
  region: string;
  callingCode: string;
  /** Name in the active locale, falling back to English, then the ISO code. */
  name: string;
  /** English name, kept for search only. */
  englishName: string;
}

type NameResolver = { of: (code: string) => string | undefined };

const cache = new Map<string, CountryOption[]>();

let ready: Promise<void> | undefined;

/**
 * Bumped whenever the names change under a caller's feet.
 *
 * `countryOptions` is synchronous and cached, but the polyfill that gives it
 * real names is not. A picker that renders first caches 250 ISO codes, and
 * clearing the cache afterwards does nothing on its own -- React has no reason
 * to call again. That was the whole bug: the polyfill installed, the list stayed
 * `IL`, and searching for "ישראל" matched nothing.
 *
 * Deliberately not a React API. This package has no React dependency and the two
 * clients render differently; each pairs these with `useSyncExternalStore`.
 */
let version = 0;
const listeners = new Set<() => void>();

export function countryNamesVersion(): number {
  return version;
}

export function subscribeCountryNames(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function namesChanged(): void {
  // Anything built before the names existed is now wrong.
  cache.clear();
  version += 1;
  for (const listener of listeners) listener();
}

/**
 * Install `Intl.DisplayNames` if the runtime has none, and only then.
 *
 * Async and dynamically imported, for two reasons. It has to be ESM — these
 * packages are `"type": "module"`, so a `require()` here throws
 * "require is not defined" and the polyfill silently never installs, which
 * reproduces the original bug with a warning instead of a fix. And browsers all
 * ship the real API, so a static import would add roughly a megabyte of locale
 * data to the web bundle to serve a code path it never takes.
 *
 * Call it once at startup, before the picker can be opened. Safe to call more
 * than once; safe to call on a runtime that needs nothing.
 */
export function ensureCountryNames(): Promise<void> {
  ready ??= install();
  return ready;
}

async function install(): Promise<void> {
  if (typeof (Intl as { DisplayNames?: unknown }).DisplayNames === 'function') return;

  try {
    // `Intl.DisplayNames` is not the only thing Hermes is missing, and the
    // polyfill does not carry its own dependencies. `__addLocaleData` calls
    // `new Intl.Locale(locale).minimize()`, and the constructor canonicalises
    // through `Intl.getCanonicalLocales`. Neither exists on the phone.
    //
    // Hermes implements `new` by reading `.prototype` off the callee, so the
    // missing constructor surfaced as `Cannot read property 'prototype' of
    // undefined` thrown from inside a vendor file -- and because the locale-data
    // modules are evaluated by Metro's async require, the throw escaped the
    // `catch` below and killed the app at startup instead of degrading.
    //
    // Both self-install only when the runtime needs them, so this costs a
    // browser nothing but the two dynamic imports.
    // The `.js` is required, as it is on the locale data below: neither package
    // declares `exports`, and Node's ESM resolver does not guess extensions for
    // a bare subpath the way Metro does.
    await import('@formatjs/intl-getcanonicallocales/polyfill.js');
    await import('@formatjs/intl-locale/polyfill.js');

    // Check rather than trust. A throw raised while Metro evaluates a module
    // cannot be caught here at all: `guardedLoadModule` hands it to
    // `ErrorUtils.reportFatalError` and swallows it, so the `catch` below never
    // runs and the app dies on a blank screen. Refusing to load the locale data
    // is the only way to keep the documented behaviour -- degrade to ISO codes,
    // stay alive -- on a runtime missing something we did not anticipate.
    if (!hasIntl('Locale') || !hasIntl('getCanonicalLocales')) {
      console.warn('[vav] country names unavailable: Intl polyfills did not install');
      return;
    }

    // Order is load-bearing. The locale-data modules export nothing: each one
    // checks for `Intl.DisplayNames.__addLocaleData` and registers itself. Import
    // them before the polyfill is on `Intl` and they silently do nothing, which
    // leaves every country showing as its ISO code -- the original bug, restored.
    const { DisplayNames } = await import('@formatjs/intl-displaynames');
    (Intl as { DisplayNames?: unknown }).DisplayNames = DisplayNames;

    await Promise.all([
      import('@formatjs/intl-displaynames/locale-data/en.js'),
      import('@formatjs/intl-displaynames/locale-data/he.js'),
      import('@formatjs/intl-displaynames/locale-data/ar.js'),
      import('@formatjs/intl-displaynames/locale-data/es.js'),
      import('@formatjs/intl-displaynames/locale-data/pt.js'),
      import('@formatjs/intl-displaynames/locale-data/hi.js'),
      import('@formatjs/intl-displaynames/locale-data/ja.js'),
    ]);

    namesChanged();
  } catch (err) {
    // Names degrade to ISO codes: bad, but the app still works and search still
    // matches the code and the calling code.
    console.warn('[vav] no country names available', err);
  }
}

function hasIntl(key: 'Locale' | 'getCanonicalLocales'): boolean {
  return typeof (Intl as Record<string, unknown>)[key] === 'function';
}

function resolver(locale: string): NameResolver | undefined {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    return undefined;
  }
}

export function countryOptions(locale: string): CountryOption[] {
  const hit = cache.get(locale);
  if (hit) return hit;

  const localised = resolver(locale);
  const english = resolver('en');

  const options = countryDialInfo()
    .map(({ region, callingCode }) => {
      const englishName = safeName(english, region) ?? region;
      return {
        region,
        callingCode,
        name: safeName(localised, region) ?? englishName,
        englishName,
      };
    })
    // Sorted by the name the user sees, in their own collation: alphabetical in
    // Latin is not alphabetical in Hebrew or Japanese.
    .sort((a, b) => compare(a.name, b.name, locale));

  cache.set(locale, options);
  return options;
}

/**
 * A national number plus a chosen region, as one unambiguous international
 * number.
 *
 * Lives here because three callers need it — both phone fields and signup — and
 * the copies had already started to differ. Prefixing the calling code at the
 * edge means nothing downstream ever has to guess a region, which is what made
 * `054…` fail on a phone whose language carried no country.
 */
export function composeInternational(
  locale: string,
  national: string,
  region: string | undefined,
): string {
  const trimmed = national.trim();
  const option = countryFor(locale, region);
  if (!option) return trimmed;
  // Drop a trunk zero the user may have typed out of habit; libphonenumber
  // tolerates it, but stripping it keeps what we send honest.
  return `+${option.callingCode}${trimmed.replace(/^0+/, '')}`;
}

export function countryFor(locale: string, region: string | undefined): CountryOption | undefined {
  const info = dialInfoFor(region);
  if (!info) return undefined;
  return countryOptions(locale).find((option) => option.region === info.region);
}

/**
 * Match a query against everything a person might reasonably type.
 *
 * Diacritics are stripped both sides so "espana" finds "España" — a real
 * problem for anyone whose keyboard is not set to their own language.
 */
export function matchesCountry(option: CountryOption, query: string): boolean {
  const needle = fold(query);
  if (!needle) return true;

  return (
    fold(option.name).includes(needle) ||
    fold(option.englishName).includes(needle) ||
    option.region.toLowerCase() === needle ||
    option.callingCode.startsWith(needle.replace(/^\+/, ''))
  );
}

function fold(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

function safeName(source: NameResolver | undefined, region: string): string | undefined {
  try {
    const name = source?.of(region);
    // The API returns the input unchanged when it has no name for it.
    return name && name !== region ? name : undefined;
  } catch {
    return undefined;
  }
}

function compare(a: string, b: string, locale: string): number {
  try {
    return a.localeCompare(b, locale);
  } catch {
    return a.localeCompare(b);
  }
}
